/**
 * `ProbeCard` — one Monitor 2.0 probe: big status-colored live value,
 * min/max/avg for the selected range, and an area chart with the
 * target band shaded. Live values arrive via the WS subscription; the
 * range window is backfilled from /api/timeseries. In customize mode
 * the card grows hide / move / size / target-editor controls — all
 * display-only preferences, nothing here writes to hardware.
 */
import { useEffect, useMemo, useState } from 'react';
import {
  Area,
  AreaChart,
  CartesianGrid,
  ReferenceArea,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { computeSeriesStats } from '@reefapp/shared';

import { useHistoricalData, useTimeSeriesSubscription } from '../hooks/useWebSocket';
import type { TimeSeriesPoint } from '../lib/timeseries-api';
import {
  findPreset,
  statusOf,
  type ProbeSize,
  type ProbeTarget,
  type TargetStatus,
} from './monitorConfig';

const DAY_MS = 24 * 60 * 60 * 1_000;
const MAX_LIVE_POINTS = 500;

export interface ProbeCardProps {
  /** Raw Apex probe name — also the timeseries sensorId. */
  readonly id: string;
  readonly probeType: string | null;
  readonly unit: string | null;
  /** Latest snapshot value, or `null` when the probe is absent from the snapshot. */
  readonly liveValue: number | null;
  readonly target: ProbeTarget | null;
  readonly size: ProbeSize;
  readonly fromMs: number;
  readonly toMs: number;
  readonly customize: boolean;
  readonly canMoveUp: boolean;
  readonly canMoveDown: boolean;
  readonly onHide: () => void;
  readonly onMove: (dir: -1 | 1) => void;
  readonly onToggleSize: () => void;
  readonly onTargetChange: (target: ProbeTarget | null) => void;
}

function fmt(v: number, digits = 2): string {
  return Number.isInteger(v) ? String(v) : v.toFixed(digits);
}

function tsFormatter(windowMs: number): (ts: number) => string {
  if (windowMs <= 2 * DAY_MS) {
    return (ts) =>
      new Date(ts).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
  }
  return (ts) =>
    new Date(ts).toLocaleDateString([], { month: 'numeric', day: 'numeric' });
}

const VALUE_TEXT: Record<TargetStatus, string> = {
  good: 'text-emerald-300',
  warn: 'text-amber-300',
  bad: 'text-red-400',
  none: 'text-reef-100',
};

const CTRL_BTN =
  'rounded-md border border-reef-700 bg-reef-800 px-2 py-1 text-[11px] font-semibold ' +
  'text-reef-200 hover:bg-reef-700 disabled:opacity-40 disabled:pointer-events-none';

const NUM_INPUT =
  'w-20 rounded-md border-reef-700 bg-reef-950 px-2 py-1 text-xs text-reef-100 ' +
  'focus:border-amber-500 focus:ring-amber-500';

export function ProbeCard(props: ProbeCardProps) {
  const hist = useHistoricalData('apex', props.id, props.fromMs, props.toMs, 500);
  const { latest } = useTimeSeriesSubscription('apex', props.id);

  // Live points received since mount, appended after the backfill so
  // the chart keeps moving between range refetches.
  const [livePoints, setLivePoints] = useState<TimeSeriesPoint[]>([]);
  useEffect(() => {
    setLivePoints([]);
  }, [props.id, props.fromMs, props.toMs]);
  useEffect(() => {
    if (latest === null) return;
    setLivePoints((prev) => {
      const last = prev[prev.length - 1];
      if (last !== undefined && last.ts >= latest.ts) return prev;
      const next = [...prev, latest];
      return next.length > MAX_LIVE_POINTS ? next.slice(-MAX_LIVE_POINTS) : next;
    });
  }, [latest]);

  const points = useMemo(() => {
    const lastHistTs =
      hist.points.length > 0
        ? hist.points[hist.points.length - 1]!.ts
        : Number.NEGATIVE_INFINITY;
    const fresh = livePoints.filter((p) => p.ts > lastHistTs);
    return fresh.length > 0 ? [...hist.points, ...fresh] : hist.points;
  }, [hist.points, livePoints]);

  const stats = useMemo(() => computeSeriesStats(points), [points]);

  // Prefer the freshest source for the headline value: WS tick > snapshot.
  const current = latest !== null ? latest.value : props.liveValue;
  const status: TargetStatus = current === null ? 'none' : statusOf(current, props.target);

  // Y domain pads a touch beyond data ∪ target band so the band shows.
  const yDomain = useMemo<[number, number] | null>(() => {
    if (stats === null) return null;
    const lo = props.target === null ? stats.min : Math.min(stats.min, props.target.min);
    const hi = props.target === null ? stats.max : Math.max(stats.max, props.target.max);
    const pad = (hi - lo) * 0.08 || Math.abs(hi) * 0.02 || 1;
    return [lo - pad, hi + pad];
  }, [stats, props.target]);

  const formatTs = tsFormatter(props.toMs - props.fromMs);

  // ── Target editor (customize mode) ─────────────────────────────────
  const [editing, setEditing] = useState(false);
  const [minText, setMinText] = useState('');
  const [maxText, setMaxText] = useState('');
  useEffect(() => {
    if (!props.customize) setEditing(false);
  }, [props.customize]);

  const preset = useMemo(
    () => findPreset(props.id, props.probeType ?? undefined),
    [props.id, props.probeType],
  );

  const openEditor = (): void => {
    setMinText(props.target !== null ? String(props.target.min) : '');
    setMaxText(props.target !== null ? String(props.target.max) : '');
    setEditing(true);
  };
  const parsedMin = Number(minText);
  const parsedMax = Number(maxText);
  const editorValid =
    minText.trim() !== '' &&
    maxText.trim() !== '' &&
    Number.isFinite(parsedMin) &&
    Number.isFinite(parsedMax) &&
    parsedMin <= parsedMax;
  const saveTarget = (): void => {
    if (!editorValid) return;
    props.onTargetChange({ min: parsedMin, max: parsedMax });
    setEditing(false);
  };
  const clearTarget = (): void => {
    props.onTargetChange(null);
    setEditing(false);
  };
  const applyPreset = (): void => {
    if (preset === null) return;
    props.onTargetChange({ ...preset.target });
    setEditing(false);
  };

  const chartHeight = props.size === 'lg' ? 'h-56 sm:h-64' : 'h-36 sm:h-44';

  return (
    <section
      data-testid={`probe-card-${props.id}`}
      className={`rounded-xl border border-reef-800 bg-reef-900 p-4 ${
        props.size === 'lg' ? 'md:col-span-2' : ''
      }`}
    >
      {/* Header: name + big value */}
      <div className="flex items-start justify-between gap-2 flex-wrap">
        <div>
          <h3 className="text-xs font-semibold uppercase tracking-wider text-reef-400">
            {props.id}
          </h3>
          {current === null ? (
            <p
              data-testid={`probe-waiting-${props.id}`}
              className="mt-1 text-sm text-reef-500"
            >
              waiting for Apex…
            </p>
          ) : (
            <div className="mt-1 flex items-baseline gap-1.5">
              <span
                data-testid={`probe-value-${props.id}`}
                className={`text-3xl font-bold tabular-nums ${VALUE_TEXT[status]}`}
              >
                {fmt(current)}
              </span>
              {props.unit !== null && props.unit !== '' ? (
                <span className="text-sm text-reef-400">{props.unit}</span>
              ) : null}
            </div>
          )}
        </div>
        {props.target !== null ? (
          <span className="rounded-full bg-reef-800 px-2.5 py-1 text-[11px] font-semibold text-reef-300">
            target {fmt(props.target.min)}–{fmt(props.target.max)}
          </span>
        ) : null}
      </div>

      {/* Customize controls */}
      {props.customize ? (
        <div className="mt-3 flex flex-wrap items-center gap-1.5">
          <button
            type="button"
            data-testid={`probe-hide-${props.id}`}
            onClick={props.onHide}
            className={CTRL_BTN}
          >
            Hide
          </button>
          <button
            type="button"
            data-testid={`probe-up-${props.id}`}
            onClick={() => props.onMove(-1)}
            disabled={!props.canMoveUp}
            aria-label={`Move ${props.id} up`}
            className={CTRL_BTN}
          >
            ↑
          </button>
          <button
            type="button"
            data-testid={`probe-down-${props.id}`}
            onClick={() => props.onMove(1)}
            disabled={!props.canMoveDown}
            aria-label={`Move ${props.id} down`}
            className={CTRL_BTN}
          >
            ↓
          </button>
          <button
            type="button"
            data-testid={`probe-size-${props.id}`}
            onClick={props.onToggleSize}
            className={CTRL_BTN}
          >
            {props.size === 'md' ? 'Large card' : 'Small card'}
          </button>
          <button
            type="button"
            data-testid={`probe-target-edit-${props.id}`}
            onClick={() => (editing ? setEditing(false) : openEditor())}
            className={CTRL_BTN}
          >
            Edit target
          </button>
        </div>
      ) : null}

      {/* Inline target editor */}
      {props.customize && editing ? (
        <div className="mt-2 flex flex-wrap items-center gap-2 rounded-lg border border-reef-700 bg-reef-950/60 p-2">
          <label className="flex items-center gap-1 text-[11px] text-reef-400">
            min
            <input
              type="number"
              step="any"
              value={minText}
              onChange={(e) => setMinText(e.target.value)}
              data-testid={`probe-target-min-${props.id}`}
              className={NUM_INPUT}
            />
          </label>
          <label className="flex items-center gap-1 text-[11px] text-reef-400">
            max
            <input
              type="number"
              step="any"
              value={maxText}
              onChange={(e) => setMaxText(e.target.value)}
              data-testid={`probe-target-max-${props.id}`}
              className={NUM_INPUT}
            />
          </label>
          <button
            type="button"
            data-testid={`probe-target-save-${props.id}`}
            onClick={saveTarget}
            disabled={!editorValid}
            className={CTRL_BTN}
          >
            Save
          </button>
          {preset !== null ? (
            <button
              type="button"
              data-testid={`probe-target-preset-${props.id}`}
              onClick={applyPreset}
              title={`Apply reef preset ${preset.label}`}
              className={CTRL_BTN}
            >
              Preset {preset.label}
            </button>
          ) : null}
          {props.target !== null ? (
            <button
              type="button"
              data-testid={`probe-target-clear-${props.id}`}
              onClick={clearTarget}
              className={CTRL_BTN}
            >
              Clear target
            </button>
          ) : null}
        </div>
      ) : null}

      {/* Min / Avg / Max for the selected range */}
      {stats !== null ? (
        <div className="mt-3 grid grid-cols-3 gap-2 text-center">
          {(
            [
              ['Min', stats.min],
              ['Avg', stats.avg],
              ['Max', stats.max],
            ] as const
          ).map(([label, v]) => (
            <div key={label} className="rounded-md bg-reef-950/60 px-2 py-1.5">
              <div className="text-[10px] uppercase tracking-wider text-reef-500">
                {label}
              </div>
              <div className="text-sm font-semibold tabular-nums text-reef-200">
                {fmt(v)}
              </div>
            </div>
          ))}
        </div>
      ) : null}

      {/* Chart */}
      <div className={`mt-3 ${chartHeight}`}>
        {hist.loading && points.length === 0 ? (
          <div className="flex h-full items-center justify-center text-xs text-reef-500">
            Loading history…
          </div>
        ) : hist.error !== null && points.length === 0 ? (
          <div className="flex h-full items-center justify-center text-xs text-red-400">
            {hist.error}
          </div>
        ) : points.length < 2 ? (
          <div className="flex h-full items-center justify-center text-xs text-reef-500">
            Not enough history yet for this window.
          </div>
        ) : (
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={points} margin={{ top: 4, right: 8, bottom: 0, left: -10 }}>
              <CartesianGrid stroke="#1e293b" strokeDasharray="3 3" />
              <XAxis
                dataKey="ts"
                type="number"
                domain={[props.fromMs, 'dataMax']}
                tickFormatter={formatTs}
                tick={{ fill: '#64748b', fontSize: 10 }}
                stroke="#334155"
              />
              <YAxis
                domain={yDomain ?? ['auto', 'auto']}
                tick={{ fill: '#64748b', fontSize: 10 }}
                stroke="#334155"
                width={48}
                tickFormatter={(v: number) => fmt(v, 1)}
              />
              <Tooltip
                contentStyle={{
                  backgroundColor: '#0f172a',
                  border: '1px solid #334155',
                  borderRadius: 8,
                  fontSize: 12,
                }}
                labelStyle={{ color: '#94a3b8' }}
                labelFormatter={(ts) => new Date(Number(ts)).toLocaleString()}
                formatter={(value) => [
                  `${fmt(Number(value))} ${props.unit ?? ''}`.trim(),
                  props.id,
                ]}
              />
              {props.target !== null ? (
                <ReferenceArea
                  y1={props.target.min}
                  y2={props.target.max}
                  fill="#10b981"
                  fillOpacity={0.08}
                  stroke="#10b981"
                  strokeOpacity={0.25}
                  strokeDasharray="4 4"
                />
              ) : null}
              <Area
                type="monotone"
                dataKey="value"
                stroke="#f59e0b"
                strokeWidth={2}
                fill="#f59e0b"
                fillOpacity={0.12}
                dot={false}
                isAnimationActive={false}
              />
            </AreaChart>
          </ResponsiveContainer>
        )}
      </div>
    </section>
  );
}
